import { DateRangePickerProps } from "@amzn/awsui-components-console";
import moment from "moment-timezone";
import { SEARCH_PARAMETERS } from "../searchResults/constants";

/**
 * Types and helpers the three pre-config claims history components still need.
 * They lived in `ClaimsSearchBar` while that component owned search state; the
 * config-driven bar takes its fields from a `SearchConfig` and reports the
 * picker value upwards, so nothing here is reachable from it.
 *
 * This module is deleted together with `WorkersCompensationClaimsHistory`,
 * `CargoClaimsHistory`, and `IPLClaimsHistory` once the config-driven page has
 * been live in prod for a full on-call rotation.
 */

export interface SearchByLabel {
  buttonText: string;
  buttonTextSmall: string;
  placeholder: string;
  placeholderSmall: string;
}

export interface LineOfBusinessConfig {
  searchByLabels: { [key: string]: SearchByLabel };
  availableSearchParameters: SEARCH_PARAMETERS[];
  routePath: string;
  metricsPrefix: string;
}

export interface SearchParameters {
  searchBy: SEARCH_PARAMETERS;
  searchTerm: string;
  startDate?: number;
  endDate?: number;
}

/**
 * The one-year window the search bar used to seed on mount. Retained only so a
 * site search on the flag-off path keeps bounding its results the way it does
 * today; the config-driven page starts with no range selected.
 */
export function legacyDefaultDateRange(): DateRangePickerProps.Value {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - 1);
  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
  return {
    startDate: new Date(startDate.getTime() - timezoneOffset).toISOString().substring(0, 10),
    endDate: new Date(endDate.getTime() - timezoneOffset).toISOString().substring(0, 10),
    type: "absolute",
  };
}

/**
 * Rebuilds a picker value from the epoch millisecond bounds a results URL
 * carries, so a reloaded or shared link shows the range its results are for.
 * The search bar used to read these query parameters itself; the controlled bar
 * takes its value from the page, so the page reconstructs it here.
 */
export function pickerValueFromEpochBounds(
  startDateMs: number,
  endDateMs: number
): DateRangePickerProps.Value {
  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
  return {
    startDate: new Date(startDateMs - timezoneOffset).toISOString().substring(0, 10),
    endDate: new Date(endDateMs - timezoneOffset).toISOString().substring(0, 10),
    type: "absolute",
  };
}

/**
 * Converts a picker value to the epoch millisecond bounds the legacy
 * `listWcClaims` request carries. Zeros stand for "no bounds", which is how the
 * legacy callers already read an unset range.
 */
export function toEpochBounds(dateRange: DateRangePickerProps.Value | null): {
  startDate: number;
  endDate: number;
} {
  if (dateRange?.type === "absolute" && dateRange.startDate && dateRange.endDate) {
    // The timezone offset is added to match the UTC timestamps the API expects.
    const startDate = Date.parse(dateRange.startDate) + new Date().getTimezoneOffset() * 60000;
    const parsedEnd = Date.parse(dateRange.endDate) + new Date().getTimezoneOffset() * 60000;

    // Advance to the following midnight and step back 1 ms to land on
    // 23:59:59.999 of the selected end date, without exceeding the present.
    const endDateMidnight = new Date(parsedEnd);
    endDateMidnight.setDate(endDateMidnight.getDate() + 1);
    const endDate =
      endDateMidnight.getTime() < Date.now() ? endDateMidnight.getTime() - 1 : Date.now();

    return { startDate, endDate };
  }

  if (dateRange?.type === "relative" && dateRange.unit && dateRange.amount) {
    return {
      startDate: moment().subtract(dateRange.amount, dateRange.unit).valueOf(),
      endDate: moment.now(),
    };
  }

  return { startDate: 0, endDate: 0 };
}
